import { useState } from 'react';
import { useNavigate } from 'react-router';
import { failureOf, said } from '@/api/errors';
import { useAssignees, useAssignTask, useCreateTask, useCompanies, useContacts, useProjects, type WireAssignee, type WireAssignees } from '@/api/hooks';
import type { WireTask } from '@/api/wire';
import { useFrameMaybe } from '@/app/frame';
import { useMeOrThrow } from '@/app/MeContext';
import { Dialog } from '@/ui/Dialog';
import { Select, Textarea } from '@/ui/fields';
import { Button, Field, TextInput } from '@/ui/primitives';
import { useToast } from '@/ui/Toast';

/**
 * Who does a task (ADR-0157): you, someone who works in this department, or
 * one of its own agents. The list is the platform's answer for the
 * department, never one this screen keeps.
 */

/** A choice in the picker, as the value a picker holds. */
export function assigneeValue(a: WireAssignee): string {
  return 'me' in a ? 'me' : 'humanId' in a ? `h:${a.humanId}` : `a:${a.agentId}`;
}

export function assigneeOfValue(v: string): WireAssignee {
  if (v.startsWith('h:')) return { humanId: v.slice(2) };
  if (v.startsWith('a:')) return { agentId: v.slice(2) };
  return { me: true };
}

/** The options, grouped as a person reads them: me, the people here, the agents here. */
export function assigneeOptions(list: Pick<WireAssignees, 'people' | 'agents'> | undefined, myId: string): Array<{ value: string; label: string }> {
  const people = (list?.people ?? []).filter((p) => p.id !== myId);
  return [
    { value: 'me', label: 'Me' },
    ...people.map((p) => ({
      value: `h:${p.id}`,
      label: `${p.displayName}${p.isLead ? ' · leads here' : ' · member'}${p.openTasks===undefined?'':` · ${p.openTasks} open`}${p.away ? (p.away.capacityPct === 0 ? ` · away until ${p.away.endsOn}` : ` · ${p.away.capacityPct}% until ${p.away.endsOn}`) : ''}`,
    })),
    ...(list?.agents ?? []).map((a) => ({ value: `a:${a.id}`, label: `${a.givenName} Agent${a.openTasks===undefined?'':` · ${a.openTasks} open`}${a.tools===undefined?'':` · ${a.tools.length} tools`}${a.canWork === false ? ' · cannot work tasks yet' : ''}` })),
  ];
}

/** The name a task's holder goes by on a card, or null when it is the viewer's own. */
export function holderOf(task: Pick<WireTask, 'assigneeHuman' | 'assigneeAgent'>, list: Pick<WireAssignees, 'people' | 'agents'> | undefined, myId: string): string | null {
  if (task.assigneeHuman === myId) return null;
  if (task.assigneeAgent) {
    const a = list?.agents.find((x) => x.id === task.assigneeAgent);
    return a ? `${a.givenName} Agent` : 'An agent';
  }
  if (task.assigneeHuman) return list?.people.find((p) => p.id === task.assigneeHuman)?.displayName ?? 'A colleague';
  return 'Nobody yet';
}

export function NewTask({ onClose, initialTitle = '', initialBody = '', stay = false, initialCompanyId = '', initialContactId = '' }: { onClose: () => void; initialTitle?: string; initialBody?: string; stay?: boolean; initialCompanyId?: string; initialContactId?: string }) {
  const me = useMeOrThrow();
  const navigate = useNavigate();
  const toast = useToast();
  const assignees = useAssignees(me.department);
  const create = useCreateTask();
  const [title, setTitle] = useState(initialTitle);
  const [body, setBody] = useState(initialBody);
  const [who, setWho] = useState('me');
  const [due, setDue] = useState('');
  const [priority, setPriority] = useState('3');
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState(initialCompanyId);
  const [contactId, setContactId] = useState(initialContactId);
  const [projectId, setProjectId] = useState('');
  const [companyQuery, setCompanyQuery] = useState('');
  const [contactQuery, setContactQuery] = useState('');
  const companies = useCompanies(companyQuery);
  const contacts = useContacts(contactQuery);
  const projects = useProjects(me.department);
  const agentChosen = who.startsWith('a:');
  const idle = agentChosen && assignees.data?.agents.find((a) => `a:${a.id}` === who)?.canWork === false;

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="A task here"
      title="New task"
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Make the task',
        disabled: create.isPending,
        onClick: () => {
          if (title.trim() === '') return setError('Say what is to be done.');
          setError(null);
          create.mutate(
            {
              title: title.trim(),
              ...(body.trim() ? { body: body.trim() } : {}),
              priority: Number(priority),
              ...(due ? { dueAt: new Date(`${due}T17:00:00`).toISOString() } : {}),
              assignee: assigneeOfValue(who),
              companyId: companyId || null,
              contactId: contactId || null,
              projectId: projectId || null,
            },
            {
              onSuccess: (r) => {
                toast({ text: r.created ? (who === 'me' ? 'On your board.' : `With ${r.assignedTo} now.`) : `${r.assignedTo} already has this task.` });
                onClose();
                // Made from a page the person is still working through, it stays there.
                if (!stay) void navigate(`/tasks/${r.taskId}`);
              },
              onError: (e) => setError(said(failureOf(e))),
            },
          );
        },
      }}
    >
      <Field label="What is to be done">
        <TextInput value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="Ask La Abuela Carmen for a new price list, in writing" />
      </Field>
      <Field label="Company (optional)">
        <TextInput aria-label="Find company" placeholder="Search companies" value={companyQuery} onChange={(e) => setCompanyQuery(e.target.value)} />
        <Select aria-label="Task company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} options={[{ value: '', label: 'No company' }, ...(companyId && !(companies.data?.companies ?? []).some((c) => c.id === companyId) ? [{ value: companyId, label: 'Selected company' }] : []), ...(companies.data?.companies ?? []).map((c) => ({ value: c.id, label: c.displayName ?? c.legalName }))]} />
      </Field>
      <Field label="Contact (optional)">
        <TextInput aria-label="Find contact" placeholder="Search contacts" value={contactQuery} onChange={(e) => setContactQuery(e.target.value)} />
        <Select aria-label="Task contact" value={contactId} onChange={(e) => setContactId(e.target.value)} options={[{ value: '', label: 'No contact' }, ...(contactId && !(contacts.data?.contacts ?? []).some((c) => c.id === contactId) ? [{ value: contactId, label: 'Selected contact' }] : []), ...(contacts.data?.contacts ?? []).map((c) => ({ value: c.id, label: c.fullName }))]} />
      </Field>
      <Field label="Project (optional)"><Select aria-label="Task project" value={projectId} onChange={(e) => setProjectId(e.target.value)} options={[{ value: '', label: 'No project' }, ...(projects.data?.projects ?? []).map((p) => ({ value: p.id, label: p.title }))]} /></Field>
      <p className="m-0 text-dense text-meta">New work goes into the task list below your calendar. Schedule it or ask your twin to plan it afterwards.</p>
      <Field
        label="Who does it"
        hint={
          idle
            ? 'This agent holds no model to think with yet, so it could not start the task. Give it to a person, or ask the superadmin to grant it one.'
            : agentChosen
              ? 'The agent starts on it at the next beat. What it proposes waits for your lead while the department acts only on approval.'
              : 'Someone who works here, or one of its agents. Someone in another department is asked through a request.'
        }
      >
        <Select aria-label="Who does it" value={who} onChange={(e) => setWho(e.target.value)} options={assigneeOptions(assignees.data, me.humanId)} />
      </Field>
      <Field label="Detail" hint="What is meant, and what done looks like. Sealed: only this department reads it.">
        <Textarea aria-label="Detail" value={body} onChange={(e) => setBody(e.target.value)} rows={4} maxLength={8000} />
      </Field>
      <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
        <Field label="Due">
          <TextInput type="date" value={due} onChange={(e) => setDue(e.target.value)} />
        </Field>
        <Field label="Priority">
          <Select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            options={[
              { value: '1', label: '1 · first' },
              { value: '2', label: '2' },
              { value: '3', label: '3 · usual' },
              { value: '4', label: '4' },
              { value: '5', label: '5 · last' },
            ]}
          />
        </Field>
      </div>
      {error && <p className="m-0 text-dense text-danger">{error}</p>}
    </Dialog>
  );
}

/**
 * "Give it to my twin". A twin's task is planned by the twin and confirmed by
 * its person before anything runs (Amendment 0011), so this makes no task: it
 * opens the side chat, attached to this task, with the ask written. The chat
 * turns a "look into" ask into a plan the person reads and confirms. A twin
 * only looks things up and reads pages, so the words say so, rather than let
 * a person believe it will make a call or send an email. Nothing is shown to
 * a person with no twin.
 */
export function TwinHandoff({ title, taskId, onDone }: { title: string; taskId?: string | null; onDone?: () => void }) {
  const frame = useFrameMaybe();
  if (!frame?.twin) return null;
  const twin = frame.twin;
  return (
    <div className="flex flex-col gap-1" data-testid="twin-handoff">
      <div>
        <Button
          onClick={() => {
            onDone?.();
            frame.ask(`Look into this task: “${title.slice(0, 300)}”. Tell me what you find, with sources.`, taskId ? { page: 'task', taskId } : undefined);
          }}
        >
          Give it to {twin}, my twin
        </Button>
      </div>
      <p className="m-0 text-micro text-meta">{twin} writes a plan and you read it first; nothing runs until you confirm. A twin looks things up and reads pages. It cannot send, call or file anything.</p>
    </div>
  );
}

/** Hand a task on to someone else here, by the people who may change it. */
export function Reassign({ task }: { task: WireTask }) {
  const me = useMeOrThrow();
  const toast = useToast();
  const assignees = useAssignees(me.department);
  const assign = useAssignTask();
  const current = task.assigneeAgent ? `a:${task.assigneeAgent}` : task.assigneeHuman === me.humanId ? 'me' : task.assigneeHuman ? `h:${task.assigneeHuman}` : 'me';
  const [who, setWho] = useState(current);
  return (
    <section className="flex flex-col gap-3 border border-line p-4" aria-label="Who does it" data-testid="reassign">
      <Field label="Who does it" hint="Hand it to someone who works here, or to one of the department’s agents.">
        <Select aria-label="Hand it to" value={who} onChange={(e) => setWho(e.target.value)} options={assigneeOptions(assignees.data, me.humanId)} />
      </Field>
      <div>
        <Button
          variant="primary"
          disabled={who === current || assign.isPending}
          onClick={() =>
            assign.mutate(
              { taskId: task.id, assignee: assigneeOfValue(who) },
              { onSuccess: (r) => toast({ text: `With ${r.assignedTo} now.` }), onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) },
            )
          }
        >
          Hand it on
        </Button>
      </div>
      {task.assigneeHuman === me.humanId && !task.assigneeAgent && <TwinHandoff title={task.title} taskId={task.id} />}
    </section>
  );
}

/**
 * Today's "Delegate" (ADR-0157): to someone here, a colleague or one of the
 * department's agents, or to another department as a request. Before this
 * it was only the second, so Otis or a colleague could not be named at all.
 * A task already on the day is handed on; anything else becomes a new task.
 */
export function Delegate({ title, taskId, onClose, onElsewhere }: { title: string; taskId?: string | null; onClose: () => void; onElsewhere: () => void }) {
  const me = useMeOrThrow();
  const toast = useToast();
  const assignees = useAssignees(me.department);
  const create = useCreateTask();
  const assign = useAssignTask();
  const options = assigneeOptions(assignees.data, me.humanId).filter((o) => o.value !== 'me');
  const [who, setWho] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const chosen = who || options[0]?.value || '';
  const done = (r: { assignedTo: string }) => {
    toast({ text: `With ${r.assignedTo} now.` });
    onClose();
  };
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Delegate"
      title="Who should do it?"
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Hand it on',
        disabled: !chosen || create.isPending || assign.isPending,
        onClick: () => {
          setError(null);
          const assignee = assigneeOfValue(chosen);
          if (taskId) assign.mutate({ taskId, assignee }, { onSuccess: done, onError: (e) => setError(said(failureOf(e))) });
          else create.mutate({ title, assignee }, { onSuccess: done, onError: (e) => setError(said(failureOf(e))) });
        },
      }}
    >
      <p className="m-0 text-body">“{title}”</p>
      {options.length > 0 ? (
        <Field label="Someone here" hint="A colleague in this department, or one of its agents.">
          <Select aria-label="Someone here" value={chosen} onChange={(e) => setWho(e.target.value)} options={options} />
        </Field>
      ) : (
        <p className="m-0 text-dense text-secondary">Nobody else works here yet, and the department has no agent.</p>
      )}
      <Button onClick={onElsewhere}>Ask another department instead</Button>
      <TwinHandoff title={title} taskId={taskId ?? null} onDone={onClose} />
      {error && <p className="m-0 text-dense text-danger">{error}</p>}
    </Dialog>
  );
}
